import { isEmpty } from "lodash";
import { Suspense, useEffect, useMemo, useState } from "react";
import { lazyNamed } from "src/utils/lazy-named";
import type { RoutePreload } from "src/router/route-preloads";

import { Button, EmptyState, Heading, Row, useToast } from "@hightouch/htui";
import {
  DecisionEngineStatus,
  type DecisionEngineChannelConfig,
  type TagsSchema,
} from "@hightouch/lib/customer-data/decision-engine/types";
import { migrateTagsSchema } from "@hightouch/lib/customer-data/decision-engine/tags";

import {
  Navigate,
  NoContextOutlet,
  type RouteObject,
  useLocation,
  useNavigate,
  useParams,
} from "src/router";
import genericPlaceholder from "src/assets/placeholders/generic.svg";
import { getValuesForKeys } from "src/utils/key-value";
import { Page } from "src/components/layout";
import { PageSpinner } from "src/components/loading";
import {
  DecisionEngineProvider,
  useDecisionEngineContext,
  type DecisionEngineContext,
} from "./decision-engine-context";
import {
  type DecisionEngineQuery,
  useCreateDecisionEngineMutation,
  useDecisionEngineQuery,
} from "src/graphql";
import { FeatureMatrices } from "./configuration/feature-matrices";
import { CreateFeatureMatrix } from "./configuration/feature-matrices/create";
import { FeatureMatrixDetail } from "./configuration/feature-matrices/detail";
import {
  AgentSetupPanel,
  AgentSetupPanelProvider,
} from "./flows/flow/agent-setup-panel";
import { useFlowContext } from "./flows/flow/flow-context";
import { useFlowMessageContext } from "src/pages/decision-engines/flows/flow/messages/message/flow-message-context";
import { sortFlows, showAgentOverviewTab } from "./flows/utils";
import { InsightsType } from "./insights/types";
import { SendFidelity } from "./insights/send-insights";
import { SendComparison } from "./insights/send-insights/send-comparison";
import { useIsAiDecisionEngineEnabled } from "./utils";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { AidPermissionedRoute } from "src/components/router/aid-permissioned-route";
import { AIDRole } from "src/components/permission/use-aid-permissions";

const Admin = lazyNamed(() => import("./admin"), "Admin");
const AdminCalculator = lazyNamed(() => import("./admin"), "AdminCalculator");
const AdminFlows = lazyNamed(() => import("./admin/flows"), "Flows");
const AdminManifestDetail = lazyNamed(
  () => import("./admin/manifest-detail"),
  "ManifestDetail",
);
const AdminChannelSettings = lazyNamed(
  () => import("./admin/channel-settings"),
  "ChannelSettings",
);
const AdminEngine = lazyNamed(() => import("./admin/engine"), "Engine");
const AdminInsights = lazyNamed(() => import("./admin/insights"), "Insights");
const AdminOutcomes = lazyNamed(() => import("./admin/outcomes"), "Outcomes");
const AdminSplits = lazyNamed(() => import("./admin/splits"), "Splits");
const AdminReports = lazyNamed(() => import("./admin/reports"), "Reports");
const AdminJobs = lazyNamed(() => import("./admin/jobs"), "Jobs");
const Channels = lazyNamed(
  () => import("./configuration/channels"),
  "Channels",
);
const Channel = lazyNamed(
  () => import("./configuration/channels/channel"),
  "Channel",
);
const AddChannel = lazyNamed(
  () => import("./configuration/channels/add-channel"),
  "AddChannel",
);
const Configuration = lazyNamed(
  () => import("./configuration"),
  "Configuration",
);
const EngineActivity = lazyNamed(
  () => import("./configuration/activity"),
  "EngineActivity",
);
const DecisionEngineFlows = lazyNamed(
  () => import("./flows"),
  "DecisionEngineFlows",
);
const AddFlow = lazyNamed(() => import("./flows/add"), "AddFlow");
const CreateDecisioningGroup = lazyNamed(
  () => import("./flows/groups/new"),
  "CreateDecisioningGroup",
);
const DecisioningGroup = lazyNamed(
  () => import("./flows/groups/group"),
  "DecisioningGroup",
);
const GroupConfiguration = lazyNamed(
  () => import("./flows/groups/configuration"),
  "GroupConfiguration",
);
const GroupAnalytics = lazyNamed(
  () => import("./flows/groups/analytics"),
  "GroupAnalytics",
);
const GroupInspector = lazyNamed(
  () => import("./flows/groups/inspector"),
  "GroupInspector",
);
const Flow = lazyNamed(() => import("./flows/flow"), "Loader");
const AddGoal = lazyNamed(() => import("./flows/flow/goals/add"), "AddGoal");
const Goal = lazyNamed(() => import("./flows/flow/goals/goal"), "Goal");
const InspectorV2 = lazyNamed(() => import("./inspector-v2"), "Loader");
const AddMessage = lazyNamed(
  () => import("./flows/flow/messages/add"),
  "AddMessage",
);
const UploadCsv = lazyNamed(
  () => import("./flows/flow/messages/upload-csv"),
  "UploadCsv",
);
const FlowOffers = lazyNamed(() => import("./flows/flow/offers"), "FlowOffers");
const AddOffer = lazyNamed(() => import("./flows/flow/offers/add"), "AddOffer");
const Offer = lazyNamed(() => import("./flows/flow/offers/offer"), "Offer");
const OffersUploadCsv = lazyNamed(
  () => import("./flows/flow/offers/upload-csv"),
  "UploadCsv",
);
const FlowMessage = lazyNamed(
  () => import("./flows/flow/messages/message"),
  "FlowMessage",
);
const MessageCortex = lazyNamed(
  () => import("./flows/flow/messages/message/cortex"),
  "MessageCortex",
);
const Content = lazyNamed(
  () => import("./flows/flow/messages/message/content"),
  "Content",
);
const MessageConfiguration = lazyNamed(
  () => import("./flows/flow/messages/message/configuration"),
  "Configuration",
);
const MessageOverview = lazyNamed(
  () => import("./flows/flow/messages/message/overview"),
  "Overview",
);
const ContentSuggestions = lazyNamed(
  () => import("./flows/flow/messages/message/content-suggestions"),
  "ContentSuggestions",
);
const FlowConfiguration = lazyNamed(
  () => import("./flows/flow/configuration"),
  "FlowConfiguration",
);
const FlowHealth = lazyNamed(() => import("./flows/flow/health"), "FlowHealth");
const FlowActivity = lazyNamed(
  () => import("./flows/flow/activity"),
  "FlowActivity",
);
const FlowRunDetail = lazyNamed(
  () => import("./flows/flow/run-detail"),
  "FlowRunDetail",
);
const FlowOverview = lazyNamed(
  () => import("./flows/flow/overview"),
  "FlowOverview",
);
const Insights = lazyNamed(() => import("./insights"), "Insights");
const CorrelationBreakdowns = lazyNamed(
  () => import("./insights/correlation-breakdowns"),
  "CorrelationBreakdowns",
);
const CreativeOverview = lazyNamed(
  () => import("./insights/creative-overview"),
  "CreativeOverview",
);
const GlobalBreakdowns = lazyNamed(
  () => import("./insights/global-breakdowns"),
  "GlobalBreakdowns",
);
const Timing = lazyNamed(() => import("./insights/timing"), "Timing");
const ExploreModalWrapper = lazyNamed(
  () => import("./insights/explore-modal"),
  "ExploreModalWrapper",
);
const DrillDownWrapper = lazyNamed(
  () => import("./insights/drill-down"),
  "DrillDownWrapper",
);
const PerformanceMatrix = lazyNamed(
  () => import("./insights/performance-matrix"),
  "PerformanceMatrix",
);
const ContentRecommendations = lazyNamed(
  () => import("./insights/content-recommendations"),
  "ContentRecommendations",
);
const DebugFlowRunPlots = lazyNamed(
  () => import("./debug/debug-flow-run-plots"),
  "DebugFlowRunPlots",
);
const AgentPrioritization = lazyNamed(
  () => import("./configuration/agent-prioritization"),
  "AgentPrioritization",
);
const Scheduling = lazyNamed(
  () => import("./configuration/scheduling"),
  "Scheduling",
);
const landingPageImport = () => import("./landing-page");
const LandingPage = lazyNamed(landingPageImport, "LandingPage");

export const preloads: RoutePreload[] = [["/ai", landingPageImport]];
const FlowMessages = lazyNamed(
  () => import("./flows/flow/messages"),
  "FlowMessages",
);
const TagsManagement = lazyNamed(
  () => import("./tags-management"),
  "TagsManagement",
);
const MessageTags = lazyNamed(
  () => import("./tags-management/message-tags"),
  "MessageTags",
);
const VariantTags = lazyNamed(
  () => import("./tags-management/variant-tags"),
  "VariantTags",
);
const ChannelTags = lazyNamed(
  () => import("./tags-management/channel-tags"),
  "ChannelTags",
);
const Reports = lazyNamed(() => import("./insights/reports"), "Reports");
const Localization = lazyNamed(
  () => import("./configuration/localization"),
  "Localization",
);
const FlowAlerting = lazyNamed(
  () => import("./flows/flow/alerting"),
  "Alerting",
);
const FlowTesting = lazyNamed(
  () => import("./flows/flow/testing"),
  "FlowTesting",
);
const FlowWeights = lazyNamed(() => import("./flows/flow/weights"), "Weights");

export type DecisionEngine = NonNullable<
  DecisionEngineQuery["decision_engines"]
>[0];

export type Channel = {
  id: string;
  type: string;
  config: DecisionEngineChannelConfig;
  messages: DecisionEngine["channels"][0]["messages"];
  enabled: boolean;
  destination: {
    id: string;
    name: string | null;
    type: string;
    definition: {
      icon: string;
      name: string;
    };
  };
  label: string | null;
};

export type Message = NonNullable<Channel>["messages"][0] & {
  channel: Channel;
};

const Loader = () => {
  const { data: engine, isLoading } = useDecisionEngineQuery(undefined, {
    select: (data) => data.decision_engines[0],
    meta: {
      isFastEndpoint: true,
    },
  });
  const decisionEngineEnabled = useIsAiDecisionEngineEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const createEngineMutation = useCreateDecisionEngineMutation();

  const createEngine = async () => {
    try {
      await createEngineMutation.mutateAsync({
        input: {
          name: "Default",
          enabled: true,
          status: DecisionEngineStatus.PENDING,
        },
      });
      navigate("/ai/flows");
    } catch {
      toast({
        id: "create-decision-engine",
        title: "Failed to set up AI Decisioning",
        variant: "error",
      });
    }
  };
  const [openSetupFlowId, setOpenSetupFlowId] = useState<string>();
  const [isSetupPanelMinimized, setIsSetupPanelMinimized] = useState(false);
  const setupPanelContext = useMemo(
    () => ({
      openSetupPanel: (flowId: string) => {
        setOpenSetupFlowId(flowId);
        setIsSetupPanelMinimized(false);
      },
    }),
    [],
  );

  const urlFlowId = location.pathname.match(
    new RegExp("/ai/flows/([^/]+)"),
  )?.[1];
  const isEnginePage =
    location.pathname.includes("/ai/admin") ||
    location.pathname.includes("/ai/configuration");
  const showSetupPanel =
    openSetupFlowId !== undefined &&
    (urlFlowId === openSetupFlowId || isEnginePage);

  // Navigating to a page where the panel doesn't show clears the minimized
  // state, so it re-opens expanded when the user returns to a relevant page.
  useEffect(() => {
    if (!showSetupPanel) {
      setIsSetupPanelMinimized(false);
    }
  }, [showSetupPanel]);

  const context: DecisionEngineContext = useMemo(() => {
    const messages =
      engine?.channels?.flatMap((channel) =>
        channel.messages.map((m) => ({ ...m, channel })),
      ) ?? [];
    const activeMessages = messages?.filter((m) => !m.deleted_at) || [];

    // Note for the variants schema, we are doing this in the flow message context
    // to limit querying all the variants in the message's variables at once.
    const messageTags = messages
      .filter((m) =>
        engine?.flows.some((f) =>
          f.messages.some((fm) => m.id === fm.message.id),
        ),
      )
      .flatMap((message) => message.tags)
      .filter((tag) => Boolean(tag) && !isEmpty(tag));

    const messageTagsSchema: TagsSchema = migrateTagsSchema(
      engine?.config?.message_tags_schema,
      getValuesForKeys(messageTags),
    );

    const variantTagsSchema: TagsSchema = migrateTagsSchema(
      engine?.config?.variant_tags_schema,
      {},
    );

    const channelTags = (engine?.channels ?? [])
      .map((c) => (c.config as unknown as DecisionEngineChannelConfig)?.tags)
      .filter(
        (tag): tag is Record<string, string> => Boolean(tag) && !isEmpty(tag),
      );

    const channelTagsSchema: TagsSchema = migrateTagsSchema(
      engine?.config?.channel_tags_schema,
      getValuesForKeys(channelTags),
    );

    const demoDefinition = engine?.config?.demo;

    return {
      engine: engine as DecisionEngine,
      demoDefinition,
      channels: engine?.channels as Channel[],
      messages,
      activeMessages,
      messageTagsSchema,
      variantTagsSchema,
      channelTagsSchema,
    };
  }, [engine]);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!decisionEngineEnabled) {
    return <LandingPage />;
  }

  return (
    <Suspense fallback={<PageSpinner />}>
      <DecisionEngineProvider value={context}>
        <AgentSetupPanelProvider value={setupPanelContext}>
          <NoContextOutlet />
          {engine && showSetupPanel && (
            <AgentSetupPanel
              flowId={openSetupFlowId}
              isMinimized={isSetupPanelMinimized}
              onMinimize={() => setIsSetupPanelMinimized(true)}
              onExpand={() => setIsSetupPanelMinimized(false)}
              onClose={() => setOpenSetupFlowId(undefined)}
            />
          )}
        </AgentSetupPanelProvider>
      </DecisionEngineProvider>
      {!engine && (
        <Page title="AI Decisioning" variant="dark">
          <Row gap={4} align="center">
            <Heading size="xl">AI Decisioning</Heading>
          </Row>
          <EmptyState
            mx={6}
            title="Set up AI Decisioning"
            message="AI Decisioning is organized into agents. You'll choose a parent model for each agent when you create it — the first agent establishes the source for this decision engine."
            imageUrl={genericPlaceholder}
            actions={
              <Button
                variant="primary"
                isLoading={
                  createEngineMutation.isLoading ||
                  createEngineMutation.isSuccess
                }
                onClick={createEngine}
              >
                Get started
              </Button>
            }
          />
        </Page>
      )}
    </Suspense>
  );
};

const Index = () => {
  const { engine } = useDecisionEngineContext();
  const decisionEngineEnabled = useIsAiDecisionEngineEnabled();

  if (!decisionEngineEnabled) {
    return <LandingPage />;
  }

  if (engine) {
    return <Navigate to="flows" replace />;
  }

  return <NoContextOutlet />;
};

const FlowIndex = () => {
  const { flow } = useFlowContext();

  if (!showAgentOverviewTab(flow)) {
    return <Navigate to="configuration" replace />;
  }

  return <Navigate to="overview" replace />;
};

const MessageIndex = () => {
  const { flowMessage } = useFlowMessageContext();

  if (!flowMessage.enabled) {
    return <Navigate to="content" replace />;
  }

  return <Navigate to="overview" replace />;
};

const InsightsViewRedirect = () => {
  const { engine } = useDecisionEngineContext();
  const { view } = useParams<{ view: InsightsType }>();

  // This includes both running and paused agents (we still want to show generated
  // charts for paused agents if they have any)
  const readyAgents = engine.flows.filter(
    (f) => f.status === "ready" || f.config.demo_data,
  );

  if (!readyAgents.length) {
    return <Navigate to="insights" replace />;
  }

  const firstAgent = sortFlows(readyAgents)[0];

  if (!view) {
    return (
      <Navigate to={`${InsightsType.Summary}/${firstAgent?.id}`} replace />
    );
  }

  return <Navigate to={`${firstAgent?.id}`} replace />;
};

const MarketingAgentRedirect = () => {
  const { sessionId } = useParams<{ sessionId: string }>();

  return (
    <Navigate
      to={sessionId ? `/agents/chats/${sessionId}` : "/agents/chats"}
      replace
    />
  );
};

export const routes: Array<RouteObject> = [
  {
    path: "ai/*",
    element: <Loader />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        index: true,
        element: <Index />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "flows",
        element: <DecisionEngineFlows />,
        children: [
          {
            path: "new",
            element: <AddFlow />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "flows/groups/new",
        element: (
          <FeatureFlagRoute
            flagKey="aidGlobalDecisioningGroupsEnabled"
            redirect="/ai/flows"
          >
            <CreateDecisioningGroup />
          </FeatureFlagRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "flows/groups/:groupId/*",
        element: (
          <FeatureFlagRoute
            flagKey="aidGlobalDecisioningGroupsEnabled"
            redirect="/ai/flows"
          >
            <DecisioningGroup />
          </FeatureFlagRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <GroupConfiguration />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "analytics",
            element: <GroupAnalytics />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "inspector",
            element: <GroupInspector />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
      {
        path: "flows/:flowId/*",
        element: <Flow />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <FlowIndex />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "overview",
            element: <FlowOverview />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "messages",
            element: <FlowMessages />,
            children: [
              {
                path: "add",
                element: <AddMessage />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "upload-csv",
                element: <UploadCsv />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "offers",
            element: (
              <FeatureFlagRoute flagKey="aidOffers">
                <FlowOffers />
              </FeatureFlagRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "add",
                element: <AddOffer />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "upload-csv",
                element: <OffersUploadCsv />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":offerId",
                element: <Offer />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "configuration",
            element: <FlowConfiguration />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "goals/add",
                element: <AddGoal />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "goals/:goalId",
                element: <Goal />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "health",
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                element: <FlowHealth />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "runs/:runId",
                element: <FlowRunDetail />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "weights",
            element: (
              <FeatureFlagRoute flagKey="aidTagWeights">
                <FlowWeights />
              </FeatureFlagRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "localization",
            element: <Localization />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "testing",
            element: <FlowTesting />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "alerting",
            element: <FlowAlerting />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "activity",
            element: (
              <AidPermissionedRoute
                minRequiredRole={AIDRole.AidAdmin}
                redirect="/ai/flows/:flowId"
              >
                <FlowActivity />
              </AidPermissionedRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
      {
        path: "tags",
        element: <TagsManagement />,
        spacesAccess: "hidden-from-limited-view",

        children: [
          {
            index: true,
            element: <Navigate to="messages" replace />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "messages",
            element: <MessageTags />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "variants",
            element: <VariantTags />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "channels",
            element: <ChannelTags />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
      {
        path: "flows/:flowId/messages/:messageId/*",
        element: <FlowMessage />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <MessageIndex />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "overview",
            element: <MessageOverview />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "tags",
            element: <MessageCortex />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "content-suggestions",
            element: <ContentSuggestions />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "content",
            element: <Content />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "configuration",
            element: <MessageConfiguration />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
      {
        path: "insights",
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <Insights />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: ":view",
            element: <InsightsViewRedirect />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            element: <Insights />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: `${InsightsType.Summary}/:flowId`,
                element: <Reports />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: `${InsightsType.UserBreakdowns}/:flowId`,
                element: <GlobalBreakdowns />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    path: "drill-down",
                    element: <DrillDownWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                  {
                    path: "explore",
                    element: <ExploreModalWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                ],
              },
              {
                path: `${InsightsType.PersonalizationInsights}/:flowId`,
                element: <CorrelationBreakdowns />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    path: "drill-down",
                    element: <DrillDownWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                  {
                    path: "explore",
                    element: <ExploreModalWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                ],
              },
              {
                path: `${InsightsType.CreativeOverview}/:flowId`,
                element: <CreativeOverview />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    path: "drill-down",
                    element: <DrillDownWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                ],
              },
              {
                path: `${InsightsType.Timing}/:flowId`,
                element: <Timing />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: `${InsightsType.PerformanceMatrix}/:flowId`,
                element: <PerformanceMatrix />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    path: "drill-down",
                    element: <DrillDownWrapper />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                ],
              },
              {
                path: `${InsightsType.ContentRecommendations}/:flowId`,
                element: <ContentRecommendations />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: `${InsightsType.SendFidelity}/:flowId`,
                element: <SendFidelity />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: `${InsightsType.SendComparison}/:flowId`,
                element: <SendComparison />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: `${InsightsType.Debug}/:flowId`,
                element: <DebugFlowRunPlots />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    path: "run/:runId",
                    element: <DebugFlowRunPlots />,
                    spacesAccess: "hidden-from-limited-view",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "configuration",
        element: <Configuration />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <Navigate to="channels" replace />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "channels",
            element: <Channels />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "add-channel",
                element: <AddChannel />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":channelId",
                element: <Channel />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "agent-prioritization",
            element: <AgentPrioritization />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "feature-matrices",
            element: <FeatureMatrices />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "scheduling",
            element: <Scheduling />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "activity",
            element: (
              <AidPermissionedRoute
                minRequiredRole={AIDRole.AidAdmin}
                redirect="/ai/configuration/channels"
              >
                <EngineActivity />
              </AidPermissionedRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
      {
        path: "configuration/feature-matrices/new",
        element: <CreateFeatureMatrix />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "configuration/feature-matrices/:featureMatrixId",
        element: <FeatureMatrixDetail />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "inspector",
        element: <InspectorV2 />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "admin/manifests/:manifestId",
        element: <AdminManifestDetail />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "admin",
        element: <Admin />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <Navigate to="engine" replace />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "engine",
            element: <AdminEngine />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "agents",
            element: <AdminFlows />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "outcomes",
            element: <AdminOutcomes />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "splits",
            element: <AdminSplits />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "channel-settings",
            element: <AdminChannelSettings />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "insights",
            element: <AdminInsights />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "calculator",
            element: <AdminCalculator />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "reports",
            element: <AdminReports />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "analytics",
            element: <AdminJobs />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
    ],
  },
  {
    path: "ai/marketing-agent",
    element: <MarketingAgentRedirect />,
    spacesAccess: "hidden-from-limited-view",
  },
  {
    path: "ai/marketing-agent/:sessionId",
    element: <MarketingAgentRedirect />,
    spacesAccess: "hidden-from-limited-view",
  },
];
